import React, { useEffect, useState } from "react";
import { Container, Row } from "react-bootstrap";
import API from "../axios";
import GalleryPasteTable from "./GalleryPasteTable";

interface VehicleVariantsProps {
    vehicleId: string;
}

const VehicleVariants = (props: VehicleVariantsProps) => {
    const [page, setPage] = useState(0);
    const [limit] = useState(10);
    const [loading, setLoading] = useState(false);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [vehicles, setVehicles] = useState<any[]>([]);
    const [selectedVariantIdToCopy, setSelectedVariantIdToCopy] = useState();

    const loadVehicleVariants = async () => {
        const query = {
            page: page + 1,
            limit,
            vehicle: props.vehicleId,
        };

        setLoading(true);
        try {
            const result: any = await API.paginateVehicleVariants(query);
            setVehicles(result.data);
            setTotalPages(result.pagination.totalPages);
            setTotalItems(result.pagination.total);
        } catch (error) {
            console.log("Failed to load vehicles: ", error);
        } finally {
            setLoading(false);
        }
    };

    const goToPage = (newPage: number) => {
        if (newPage < 1 || newPage > totalPages) return;

        // Convert to 0-based indexing for internal state
        setPage(newPage - 1);
    };

    // Load data whenever page changes
    useEffect(() => {
        loadVehicleVariants();
    }, [page]); // Add page as dependency

    // Initial load
    useEffect(() => {
        loadVehicleVariants();
    }, []);

    return (
        <div>
            <Container>
                <Row>
                    {loading ? (
                        <div
                            className="d-flex justify-content-center my-5"
                            style={{ minHeight: "100vh" }}
                        >
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">
                                    Loading...
                                </span>
                            </div>
                        </div>
                    ) : (
                        <table className="table mt-3">
                            <thead>
                                <tr>
                                    <th>Image</th>
                                    <th>Variant</th>
                                    <th>Price</th>
                                    <th>RFQ Stock</th>
                                    <th>Forecasted Stock</th>
                                    <th>Available For Sale</th>
                                    <th>Public</th>
                                    <th>Edit</th>
                                    <th>Copy Gallery</th>
                                </tr>
                            </thead>
                            <tbody>
                                {vehicles.map((vehicle, index) => (
                                    <tr key={index}>
                                        <td>
                                            <img
                                                src={
                                                    vehicle.productImage ||
                                                    "/images/no-image.png"
                                                }
                                                width={80}
                                                height={60}
                                                className="rounded-2"
                                            />
                                        </td>
                                        <td className="fw-normal">
                                            {vehicle.productAttributes.map(
                                                (productAttribute: any) => (
                                                    <div className="badge bg-primary mx-1">
                                                        {`${productAttribute.attributeName} : ${productAttribute.attributeValueName}`}
                                                    </div>
                                                )
                                            )}
                                        </td>
                                        <td>
                                            {vehicle.defaultPrice
                                                ? `AED ${vehicle.defaultPrice.toLocaleString()}`
                                                : "-"}
                                        </td>
                                        <td>{vehicle.rfqStock}</td>
                                        <td>{vehicle.forecastedStock}</td>
                                        <td>{vehicle.validStock}</td>
                                        <td>{vehicle.active ? "Yes" : "No"}</td>
                                        <td>
                                            <div className="d-grid gap-2 d-flex flex-row">
                                                <a
                                                    href={`/vehicle-variant/${vehicle._id}`}
                                                    className="btn btn-light mx-1 d-flex align-items-center justify-content-center"
                                                    style={{
                                                        width: "36px",
                                                        height: "36px",
                                                        borderRadius: "50%",
                                                    }}
                                                    title="Edit"
                                                >
                                                    <i className="bi bi-pencil"></i>
                                                </a>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="d-grid gap-2 d-flex flex-row">
                                                <button
                                                    className="btn btn-light mx-1 d-flex align-items-center justify-content-center"
                                                    data-bs-toggle="modal"
                                                    data-bs-target="#pasteModal"
                                                    onClick={()=> setSelectedVariantIdToCopy(vehicle._id)}
                                                >
                                                    <i className="bi bi-copy"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    {/* Updated Pagination */}
                    <nav
                        aria-label="Page navigation"
                        style={{ marginBottom: 200 }}
                    >
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                Showing{" "}
                                {vehicles.length > 0 ? page * limit + 1 : 0} to{" "}
                                {Math.min((page + 1) * limit, totalItems)}
                                of {totalItems} variants
                            </div>
                            <ul className="pagination">
                                <li
                                    className={`z-0 page-item ${
                                        page === 0 ? "disabled" : ""
                                    }`}
                                >
                                    <button
                                        className="page-link"
                                        onClick={() => goToPage(1)}
                                        disabled={page === 0}
                                    >
                                        First
                                    </button>
                                </li>
                                <li
                                    className={`z-0 page-item ${
                                        page === 0 ? "disabled" : ""
                                    }`}
                                >
                                    <button
                                        className="page-link"
                                        onClick={() => goToPage(page)}
                                        disabled={page === 0}
                                    >
                                        Previous
                                    </button>
                                </li>

                                {/* Page numbers - show up to 5 around current page */}
                                {totalPages > 0 &&
                                    Array.from(
                                        { length: Math.min(5, totalPages) },
                                        (_, i) => {
                                            const currentPage = page + 1; // Convert to 1-based for display
                                            let pageNum: number;
                                            if (totalPages <= 5) {
                                                pageNum = i + 1;
                                            } else if (currentPage <= 3) {
                                                pageNum = i + 1;
                                            } else if (
                                                currentPage >=
                                                totalPages - 2
                                            ) {
                                                pageNum = totalPages - 4 + i;
                                            } else {
                                                pageNum = currentPage - 2 + i;
                                            }

                                            return (
                                                <li
                                                    key={i}
                                                    className={`z-0 page-item ${
                                                        currentPage === pageNum
                                                            ? "active"
                                                            : ""
                                                    }`}
                                                >
                                                    <button
                                                        className="page-link"
                                                        onClick={() =>
                                                            goToPage(pageNum)
                                                        }
                                                    >
                                                        {pageNum}
                                                    </button>
                                                </li>
                                            );
                                        }
                                    )}

                                <li
                                    className={`z-0 page-item ${
                                        page >= totalPages - 1 ? "disabled" : ""
                                    }`}
                                >
                                    <button
                                        className="page-link"
                                        onClick={() => goToPage(page + 2)}
                                        disabled={page >= totalPages - 1}
                                    >
                                        Next
                                    </button>
                                </li>
                                <li
                                    className={`z-0 page-item ${
                                        page >= totalPages - 1 ? "disabled" : ""
                                    }`}
                                >
                                    <button
                                        className="page-link"
                                        onClick={() => goToPage(totalPages)}
                                        disabled={page >= totalPages - 1}
                                    >
                                        Last
                                    </button>
                                </li>
                            </ul>
                        </div>
                    </nav>
                </Row>
            </Container>

            {/* Paste model */}
            <div
                className="modal fade"
                id="pasteModal"
                data-bs-backdrop="static"
                data-bs-keyboard="false"
            >
                <div className="modal-dialog" style={{minWidth: 800}}>
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                Select Variant To Paste Gallery
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                            />
                        </div>
                        <div className="modal-body">
                            <GalleryPasteTable
                                vehicleId={props.vehicleId}
                                fromVariantId={selectedVariantIdToCopy}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VehicleVariants;
